import React, { useState, useEffect } from 'react';
import { userService } from "../../../_services";
import { Navigate } from 'react-router-dom';
import moment from 'moment';
import TableWidget from '../TableWidget';
import FilterTagCom from '../FilterTag.jsx';
import DateRangePicker from "../../ui/date-range-picker";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { Search, Loader2 } from "lucide-react";
import { Badge } from "../../ui/badge";

/**
 * Pharmacies Analytics Page
 * Modernized with shadcn components - Pharmacy data visualization
 */
function Pharmacies() {
    // Initialize dates
    const getInitialStartDate = () => {
        const stored = localStorage.getItem('User_Start_Date');
        if (stored) return stored;
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    };

    const getInitialEndDate = () => {
        const stored = localStorage.getItem('User_End_Date');
        return stored || new Date().toISOString().split('T')[0];
    };

    // State
    const [startDate, setStartDate] = useState(getInitialStartDate());
    const [endDate, setEndDate] = useState(getInitialEndDate());
    const [graphData, setGraphData] = useState([]);
    const [filterTagList, setFilterTagList] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [selectedTab, setSelectedTab] = useState('Total');
    const [loading, setLoading] = useState(false);

    const roleName = 'Admin';
    const whId = '61';
    const roleType = 'Admin';

    useEffect(() => {
        localStorage.setItem('Filter_Last_Update_Time', new Date());
        localStorage.setItem('pagename', 'pharmaciesPage');
        // Initialize claim type
        window.localStorage.setItem('Updated_HD_Type', 'totalpaid');
        changeData();
    }, []);

    useEffect(() => {
        changeData();
    }, [startDate, endDate]);

    const handleChangeStartEnd = (type, value) => {
        if (type === 'start') {
            setStartDate(value);
            localStorage.setItem('User_Start_Date', value);
        } else if (type === 'end') {
            setEndDate(value);
            localStorage.setItem('User_End_Date', value);
        }
    };

    const handleDateRangeChange = (event, picker) => {
        const newStartDate = picker.startDate.format('YYYY-MM-DD');
        const newEndDate = picker.endDate.format('YYYY-MM-DD');
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        localStorage.setItem('User_Start_Date', newStartDate);
        localStorage.setItem('User_End_Date', newEndDate);
    };

    const handleTabChange = (value) => {
        setSelectedTab(value);
        let claimType = 'totalpaid';
        if (value === 'Reversed') claimType = 'reversed';
        else if (value === 'Net') claimType = 'netpaid';
        else if (value === 'Paid') claimType = 'compensable';

        window.localStorage.setItem('Updated_HD_Type', claimType);
        changeData();
    };

    const handleChangeFilter = (e) => {
        if (e) {
            const cls_name = e.currentTarget?.className || '';
            const event_type = e.type;

            if (cls_name.includes('reset_filter_tag_button_event') && event_type === 'click') {
                const element = document.getElementById('totalpaid1');
                if (element) element.click();
            }

            if (localStorage.getItem('Updated_HD_Type') === 'totalpaid') {
                const element = document.getElementById('totalpaid1');
                if (element) element.click();
            }
        }
        changeData();
    };

    const changeData = () => {
        setLoading(true);
        const APIBaseURL = '/api/ws';
        const startDateFormatted = moment(startDate).format("YYYYMMDD");
        const endDateFormatted = moment(endDate).format("YYYYMMDD");

        // Parse advanced filter
        let AdFilterJson = '';
        try {
            AdFilterJson = JSON.parse(localStorage.getItem('Advance_Filter'));
            if (AdFilterJson != null && AdFilterJson != '' && AdFilterJson != undefined && AdFilterJson != "null") {
                setFilterTagList(JSON.stringify(AdFilterJson));
            } else {
                setFilterTagList('');
            }
        } catch (error) {
            setFilterTagList('');
        }

        const Pharmacy_Transactions_Widget_PostData_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateFormatted,
            end_date: endDateFormatted,
            rcount: 30,
            chart_type: "list",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        const Pharmacy_PatientSavings_Widget_PostData_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateFormatted,
            end_date: endDateFormatted,
            rcount: 30,
            chart_type: "list",
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        setGraphData([
            {
                url: APIBaseURL + "/Pharmacy_Detail_Wise_Transaction",
                type: "Table",
                title: "Transactions by Pharmacy",
                Subtitle: "Number of transactions at each Pharmacy",
                ColorCode: "#ffba10",
                XlabelString: "",
                YlabelString: "",
                id: "WPharmacyTransaction",
                RequestJson: Pharmacy_Transactions_Widget_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/Sum_PaitentSavings_By_Pharmacy",
                type: "Table",
                title: "Patient Savings by Pharmacy",
                Subtitle: "Patient savings at each Pharmacy",
                ColorCode: "#ffba10",
                XlabelString: "",
                YlabelString: "",
                id: "WPharmacyPatientSavings",
                RequestJson: Pharmacy_PatientSavings_Widget_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            }
        ]);

        setLoading(false);
    };

    // Auth check - Admin only
    const userRole = window.atob(localStorage.getItem('UserRole') || '');
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/signup" replace />;
    }
    if (userRole !== "Admin") {
        return <Navigate to="/page-not-found" replace />;
    }

    return (
        <div className="w-full space-y-6">
            {/* Heading with Beta Badge */}
            <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-brand-dark">Pharmacies</h1>
                <Badge className="bg-brand-accent text-white uppercase hover:bg-brand-accent">
                    Beta
                </Badge>
            </div>

            {/* Controls Row */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                {/* Date Range Picker */}
                <DateRangePicker
                    startDate={moment(startDate)}
                    endDate={moment(endDate)}
                    onApply={handleDateRangeChange}
                    displayMode="unified"
                />

                {/* Search and Tabs */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-dark/50" />
                        <Input
                            className="pl-10"
                            placeholder="Search"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                    </div>
                    <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full sm:w-auto">
                        <TabsList className="grid grid-cols-4 w-full">
                            <TabsTrigger value="Total">Total</TabsTrigger>
                            <TabsTrigger value="Reversed">Reversed</TabsTrigger>
                            <TabsTrigger value="Net">Net</TabsTrigger>
                            <TabsTrigger value="Paid">Paid</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {/* Main Content */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
                </div>
            ) : (
                <>
                    {/* Filter Tags */}
                    <FilterTagCom
                        Rolename={roleName}
                        wh_Id={whId}
                        Roletype={roleType}
                        df={moment(startDate).format("YYYYMMDD")}
                        dt={moment(endDate).format("YYYYMMDD")}
                        handlechangea={handleChangeFilter}
                        FilterTags={filterTagList}
                        Loading={true}
                    />

                    {/* Widgets Grid */}
                    <div className="grid grid-cols-12 gap-6 mt-6">
                        {graphData.map((widget, index) => (
                            <Card
                                key={`widget-${widget.id}-${index}`}
                                className="col-span-12 2xl:col-span-6"
                            >
                                <CardHeader className="border-b px-6 py-4">
                                    <CardTitle className="text-lg font-semibold text-brand-dark">
                                        {widget.title}
                                    </CardTitle>
                                    {widget.Subtitle && (
                                        <p className="text-sm text-brand-dark/70 mt-1">
                                            {widget.Subtitle}
                                        </p>
                                    )}
                                </CardHeader>
                                <CardContent className="p-6">
                                    <TableWidget
                                        data={widget}
                                        _Index={index}
                                        handlechangea={handleChangeFilter}
                                    />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default Pharmacies;
